import CssModuleFactory from './CssModuleFactory';
import * as cssValueMapJson from './cssValueMap.json';
import { Constants } from '../common/Constants';
import { ThemeId } from '../common/EnumTypes';
import { CssValueMap, CssValueMapItem } from '../common/InternalTypes';
import { Logger } from '../common/Logger';
import { LoggerTexts } from '../common/LoggerTexts';
import { Util } from '../common/Util';
import { ThemingConfig } from '../config/ThemingConfig';

export class CssTheming {
	private readonly _logger: Logger;
	private readonly _themingConfig: ThemingConfig;
	private _cssValueMap: CssValueMap;
	private _styleTag: HTMLStyleElement | null = null;
	private _cssBaseUrl: string;

	constructor(themingConfig: ThemingConfig) {
		this._logger = new Logger(Constants.FILE_CSS_THEMING);
		this._themingConfig = themingConfig;
		this._loadCssValueMap();
	}

	public async initialize() {
		try {
			if (this._themingConfig.writeCssInlineStyle) {
				await this._initCssInlineStyle();
			} else if (this._themingConfig.writeCssLinkTag && this._themingConfig.cssLinkTagUrl) {
				this._cssBaseUrl = Util.prepareBaseUrl(this._themingConfig.cssLinkTagUrl);
				this._updateCssLinkTag(ThemeId.none, this._cssBaseUrl);
			}
		} catch (error) {
			this._logger.error(LoggerTexts.CSS_THEMING.LOAD_CSS_MODULE_ERROR);
		}
	}

	public updateCurrentTheme(themeId: ThemeId) {
		if (this._themingConfig.writeCssInlineStyle) {
			const cssThemeValueMap = this._cssValueMap[themeId];
			if (cssThemeValueMap) {
				this._updateCSSVariables(cssThemeValueMap);
			}
		} else if (this._themingConfig.writeCssLinkTag && this._themingConfig.cssLinkTagUrl) {
			this._updateCssLinkTag(themeId, this._cssBaseUrl);
		}
	}

	private async _initCssInlineStyle() {
		if (!this._styleTag) {
			const themeCss = await CssModuleFactory.getCssModule();
			if (themeCss) {
				if (themeCss.default && themeCss.default.use) {
					themeCss.default.use();
				}
				this._styleTag = themeCss;
			}
		}
	}

	private _updateCssLinkTag(themeId: ThemeId, cssLinkTagUrl: string) {
		const documentHead = document.head;
		if (documentHead) {
			const oldLinkTag = document.getElementById(Constants.CSS_STYLE_TAG_ID);
			if (oldLinkTag) {
				oldLinkTag.remove();
			}
			const linkTag = document.createElement('link');
			linkTag.id = Constants.CSS_STYLE_TAG_ID;
			linkTag.rel = 'stylesheet';
			const linkTagUrl = new URL(`${themeId}-creative.css`, cssLinkTagUrl);
			linkTag.href = linkTagUrl.toString();
			documentHead.appendChild(linkTag);
		}
	}

	private _updateCSSVariables(themeValues: CssValueMapItem[]) {
		const root = document.documentElement;
		if (root && root.style) {
			themeValues.forEach((item: CssValueMapItem) => {
				root.style.setProperty(item.cssKey, item.cssValue.toString());
			});
		}
	}

	private _loadCssValueMap() {
		if (cssValueMapJson) {
			this._cssValueMap = cssValueMapJson as CssValueMap;
		}
	}
}
